import type { TFunction } from 'i18next';
import {
  formatCurrencyFull,
  formatDateOnly,
  formatDecimal,
  formatUnitPrice,
  hasText,
} from '~/utils';
import type { MsrPurchaseOrderField } from '~/graphql/graphql';

export type MsrFieldGroup =
  | 'overview'
  | 'dates'
  | 'people'
  | 'vendor'
  | 'shipTo'
  | 'shipping'
  | 'totals'
  | 'import'
  | 'other';

export const MSR_FIELD_GROUP_ORDER: readonly MsrFieldGroup[] = [
  'overview',
  'dates',
  'people',
  'vendor',
  'shipTo',
  'shipping',
  'totals',
  'import',
  'other',
];

const HEADER_FIELD_GROUPS: Record<string, MsrFieldGroup> = {
  purchaseOrderNumber: 'overview',
  type: 'overview',
  typeDescription: 'overview',
  status: 'overview',
  orderNumber: 'overview',
  externalSystemId: 'overview',
  isMaster: 'overview',
  isChild: 'overview',
  orderedDate: 'dates',
  createdDate: 'dates',
  shipWeekDate: 'dates',
  promiseDate: 'dates',
  dueByDate: 'dates',
  readyAtMillDate: 'dates',
  buyerCode: 'people',
  buyerName: 'people',
  orderedByCode: 'people',
  vendorCode: 'vendor',
  vendorName: 'vendor',
  vendorContact: 'vendor',
  shipFromCode: 'vendor',
  shipFromName: 'vendor',
  shipFromAddress1: 'vendor',
  shipFromAddress2: 'vendor',
  shipFromCity: 'vendor',
  shipFromState: 'vendor',
  shipFromZip: 'vendor',
  shipFromCountry: 'vendor',
  shipFromPhone: 'vendor',
  shipFromGeoArea: 'vendor',
  branchCode: 'shipTo',
  purchasingBranch: 'shipTo',
  locationCode: 'shipTo',
  shipToName: 'shipTo',
  shipToAddress1: 'shipTo',
  shipToAddress2: 'shipTo',
  shipToCity: 'shipTo',
  shipToState: 'shipTo',
  shipToZip: 'shipTo',
  shipToCountry: 'shipTo',
  shipToPhone: 'shipTo',
  shipToGeoArea: 'shipTo',
  shipVia: 'shipping',
  shipTerms: 'shipping',
  numDaysIntransit: 'shipping',
  pickupNumber: 'shipping',
  unitType: 'shipping',
  tarp: 'shipping',
  currency: 'totals',
  totalAmount: 'totals',
  volume: 'totals',
  weight: 'totals',
  isImport: 'import',
  importBOL: 'import',
  importVessel: 'import',
  importVoyage: 'import',
  importContainer: 'import',
  importVesselDischargeDate: 'import',
  importArrivalNoticeDate: 'import',
  importCustomsReleaseDate: 'import',
  importPortOfLoad: 'import',
  importPortOfDestination: 'import',
  importSteamshipLine: 'import',
  importBolHouse: 'import',
  importIsfReceived: 'import',
  importContainerCount: 'import',
  importCexmHold: 'import',
  importLastFreeDate: 'import',
  importSailingVessel: 'import',
  importSailingVoyage: 'import',
  importIncoTerms: 'import',
  importIncoTermsLoc: 'import',
  importFreightForwarder: 'import',
  importDocsReceivedDate: 'import',
  importBookingNumber: 'import',
};

export const MSR_HEADER_FIELD_KEYS = Object.keys(HEADER_FIELD_GROUPS);

export const MSR_ITEM_FIELD_KEYS = [
  'purchaseOrderItemNumber',
  'productCode',
  'productDescription',
  'productType',
  'buyCode',
  'lengthCode',
  'actualLength',
  'lineType',
  'itemStatus',
  'quantityUnitOfMeasure',
  'priceUnitOfMeasure',
  'glAccount',
  'glCenter',
  'orderedPackages',
  'orderedPiecesPerPackage',
  'orderedPiecesTotal',
  'receivedPiecesTotal',
  'invoicedPiecesTotal',
  'quantityOrdered',
  'quantityReceived',
  'quantityInvoiced',
  'plantBaseRate',
  'millRate',
  'buyCost',
  'extendedPlantCost',
  'totalAmount',
  'weight',
] as const;

type MsrValueKind =
  'text' | 'date' | 'boolean' | 'currency' | 'unitRate' | 'number';

const CURRENCY_KEYS = new Set(['totalAmount', 'extendedPlantCost']);
const UNIT_RATE_KEYS = new Set(['plantBaseRate', 'millRate', 'buyCost']);
const BOOLEAN_KEYS = new Set([
  'tarp',
  'appointmentRequired',
  'cashContract',
  'isImport',
  'isMaster',
  'isChild',
  'importCexmHold',
]);
const NUMBER_KEYS = new Set([
  'volume',
  'weight',
  'actualLength',
  'orderedPackages',
  'orderedPiecesPerPackage',
  'orderedPiecesTotal',
  'receivedPiecesTotal',
  'invoicedPiecesTotal',
  'quantityOrdered',
  'quantityReceived',
  'quantityInvoiced',
  'importContainerCount',
]);

function valueKind(key: string): MsrValueKind {
  if (key.endsWith('Date')) return 'date';
  if (BOOLEAN_KEYS.has(key) || /^is[A-Z]/.test(key)) return 'boolean';
  if (CURRENCY_KEYS.has(key)) return 'currency';
  if (UNIT_RATE_KEYS.has(key)) return 'unitRate';
  if (NUMBER_KEYS.has(key)) return 'number';
  return 'text';
}

function isImportKey(key: string): boolean {
  return key === 'isImport' || key.startsWith('import');
}

export function msrFieldGroup(key: string): MsrFieldGroup {
  return HEADER_FIELD_GROUPS[key] ?? (isImportKey(key) ? 'import' : 'other');
}

// MSR sends "0001-01-01T00:00:00" for a date that isn't set.
const MSR_UNSET_DATE_YEAR = '0001';

function datePart(value: string): string | null {
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(value);
  return match ? match[1] : null;
}

export function isBlankMsrValue(key: string, value: string | null | undefined) {
  if (!hasText(value)) return true;
  const text = value.trim();
  if (valueKind(key) !== 'date') return false;
  return datePart(text)?.startsWith(MSR_UNSET_DATE_YEAR) ?? false;
}

// MSR writes Canadian dollars as CDN, which isn't an ISO 4217 code.
export function isoCurrency(msrCurrency: string | null | undefined): string {
  const code = (msrCurrency ?? '').trim().toUpperCase();
  return code === 'CDN' ? 'CAD' : code;
}

export interface MsrFormatContext {
  currency: string | null | undefined;
  t: TFunction<'order'>;
}

function formatNumber(value: string): string {
  const parsed = Number(value);
  if (value.trim() === '' || Number.isNaN(parsed)) return value.trim();
  return formatDecimal(parsed, 4);
}

function formatMoney(
  key: string,
  value: string,
  currency: string | null | undefined
): string {
  const parsed = Number(value);
  if (value.trim() === '' || Number.isNaN(parsed)) return value.trim();
  const code = isoCurrency(currency);
  let formatted = '';
  if (/^[A-Z]{3}$/.test(code)) {
    formatted =
      valueKind(key) === 'unitRate'
        ? formatUnitPrice(parsed, code)
        : formatCurrencyFull(parsed, 2, code);
  }
  if (formatted !== '' && formatted !== 'N/A') return formatted;
  return [formatNumber(value), (currency ?? '').trim()]
    .filter((part) => part !== '')
    .join(' ');
}

export function formatMsrValue(
  key: string,
  value: string | null | undefined,
  { currency, t }: MsrFormatContext
): string {
  const kind = valueKind(key);
  if (isBlankMsrValue(key, value)) {
    return t('order.msr_purchase_order.not_set');
  }
  const text = (value ?? '').trim();
  switch (kind) {
    case 'date': {
      const day = datePart(text);
      return day ? formatDateOnly(day) : text;
    }
    case 'boolean':
      if (text === 'true') return t('order.yes');
      if (text === 'false') return t('order.no');
      return text;
    case 'currency':
    case 'unitRate':
      return formatMoney(key, text, currency);
    case 'number':
      return formatNumber(text);
    default:
      return text;
  }
}

function humanize(key: string): string {
  const words = key
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function msrFieldLabel(
  key: string,
  scope: 'header' | 'item',
  t: TFunction<'order'>
): string {
  const section = scope === 'item' ? 'item_fields' : 'fields';
  return t(`order.msr_purchase_order.${section}.${key}`, {
    defaultValue: humanize(key),
  });
}

// MSR always sends zero and false defaults for the import block, even on domestic orders.
const MSR_EMPTY_IMPORT_VALUES = new Set(['0', 'false']);

export interface MsrFieldGroupEntry {
  group: MsrFieldGroup;
  fields: MsrPurchaseOrderField[];
}

/** Groups header fields in display order; the import group appears only for imports. */
export function groupMsrFields(
  fields: readonly MsrPurchaseOrderField[]
): MsrFieldGroupEntry[] {
  const isImport =
    fields.find((field) => field.key === 'isImport')?.value === 'true';
  const hasImportData = fields.some(
    (field) =>
      isImportKey(field.key) &&
      field.key !== 'isImport' &&
      !isBlankMsrValue(field.key, field.value) &&
      !MSR_EMPTY_IMPORT_VALUES.has((field.value ?? '').trim())
  );
  return MSR_FIELD_GROUP_ORDER.map((group) => ({
    group,
    fields: fields.filter((field) => msrFieldGroup(field.key) === group),
  })).filter(
    ({ group, fields: grouped }) =>
      grouped.length > 0 && (group !== 'import' || isImport || hasImportData)
  );
}
